/**
 * i18n translation dictionary.
 * All user-visible strings live here; UI calls t(key) to fetch them.
 * When you add a new key, provide both `en` and `zh` translations.
 *
 * Key naming: <area>.<topic>.<field>
 *   header.title / header.preset.placeholder
 *   panel.section.source / panel.section.transform
 *   source.sine.name / source.sine.description
 *   transform.dft.name / transform.dft.input / transform.dft.output / transform.dft.pitfall
 *   plot.timeDomain / plot.magSpectrum
 */
export type Lang = 'en' | 'zh';

export const SUPPORTED_LANGS: Lang[] = ['en', 'zh'];

type Dict = Record<string, string>;

const en: Dict = {
  // ================= Header =================
  'header.title': 'Signal Playground',
  'header.tagline': 'Interactive signal & spectrum visualizer for EE/CS learners.',
  'header.domain.signal': '📈 Signal',
  'header.domain.image': '🖼️ Image',
  'header.domain.image.tip': 'Coming in v0.3',
  'header.preset.placeholder': '— Presets —',
  'header.preset.tip': 'Load teaching preset',
  'header.btn.cheatsheet': '📚 Cheatsheet',
  'header.btn.cheatsheet.tip': 'Show formula cheatsheet',
  'header.btn.share': '🔗 Share',
  'header.btn.share.tip': 'Copy shareable URL',
  'header.btn.png': '⬇ PNG',
  'header.btn.png.tip': 'Export plots as PNG',
  'header.btn.csv': '⬇ CSV',
  'header.btn.csv.tip': 'Export data as CSV',
  'header.btn.github': 'GitHub ↗',
  'header.btn.lang': '中文',
  'header.btn.lang.tip': 'Switch to Chinese',
  'footer.ready': 'Ready',
  'footer.meta': 'Built with ❤ for the EE/CS community · MIT License',

  // ================= Panel sections =================
  'panel.section.kind': '🧬 Signal Kind',
  'panel.section.source': '🌊 Signal Source',
  'panel.section.window': '🪟 Window Function',
  'panel.section.transform': '🔁 Transform',
  'panel.section.tfInfo': '📐 Transform Definition',
  'panel.btn.reset': '↺ Reset',
  'panel.kind.continuous': '∿ Continuous',
  'panel.kind.discrete': '┊ Discrete',
  'panel.kind.continuous.tip': 'Continuous signal x(t): high-density sampled simulation',
  'panel.kind.discrete.tip': 'Discrete signal x[n]: rendered as stem plot',
  'panel.kind.note.discrete': '<b>x[n]</b>, N = <b>{N}</b>, fs = <b>{fs} Hz</b> &rarr; t = n / fs &isin; [0, {tMax}] s.',
  'panel.kind.note.continuous': '<b>x(t)</b>, t &isin; [0, {dur}] s, sampled at <b>{fs} Hz</b> (N = {N}).',
  'panel.transform.incompatible': '✗ (incompatible with {kind})',
  'panel.transform.noParams': 'No parameters for this transform.',

  // Transform group labels
  'group.fourier-discrete': '— Fourier (Discrete) —',
  'group.fourier-continuous': '— Fourier (Continuous) —',
  'group.cosine-sine': '— Cosine / Sine —',
  'group.time-frequency': '— Time-Frequency —',
  'group.analytic': '— Analytic —',
  'group.complex-plane': '— Complex Plane —',

  // Transform info tags
  'tf.tag.input': 'Input',
  'tf.tag.output': 'Output',
  'tf.tag.pitfall': 'Pitfall',
  'tf.tag.related': 'See also',

  // ================= Formula input =================
  'formula.title': '✏️ Formula Input (LaTeX)',
  'formula.placeholder': 'e.g. \\sin(2\\pi \\cdot 5 t) + 0.5\\cos(2\\pi \\cdot 12 t)',
  'formula.invalid': '(invalid LaTeX)',
  'formula.banner.discrete': `<strong>┊ Discrete  x[n]</strong>
        Use <code>n</code> for the <b>integer index</b> (0, 1, 2, …)
        and <code>t</code> for <b>physical time in seconds</b> (t = n / fs).
        Examples: <code>\\sin(0.5\\pi n)</code> → period-4 sequence;
        <code>\\sin(2\\pi \\cdot 5 t)</code> → samples of a 5 Hz sinusoid.
        Sequence length is <b>Num Samples</b>.`,
  'formula.banner.continuous': `<strong>∿ Continuous  x(t)</strong>
        Variable <code>t</code> is <b>physical time in seconds</b> (0 → Duration);
        <code>n</code> is the integer index (n = t · fs).
        Example: <code>\\sin(2\\pi \\cdot 5 t)</code> → 5 Hz sinusoid.`,

  // ================= Plot titles / axes =================
  'plot.timeDomain.continuous': 'Time Domain  x(t)',
  'plot.timeDomain.discrete': 'Time Domain  x[n]   (N = {N})',
  'plot.axis.time': 'Time (s)',
  'plot.axis.sampleIndex': 'n  (sample index)',
  'plot.axis.amplitude': 'Amplitude',
  'plot.axis.freqHz': 'Frequency (Hz)',
  'plot.axis.omega': 'ω (rad/sample)',
  'plot.axis.harmonic': 'Harmonic k',
  'plot.axis.kExtended': 'k (extended)',
  'plot.axis.coeffIndex': 'Coefficient Index k',
  'plot.axis.value': 'Value',
  'plot.axis.magDb': 'Magnitude (dB)',
  'plot.axis.magCk': '|c_k|',
  'plot.axis.phaseRad': 'Phase (rad)',
  'plot.axis.envelope': 'Envelope',
  'plot.title.magSpectrum': 'Magnitude Spectrum',
  'plot.title.phaseSpectrum': 'Phase Spectrum',
  'plot.title.coefficients': 'Coefficients',
  'plot.title.spectrogram': 'Spectrogram (dB)',
  'plot.title.spectrogramShort': 'Spectrogram',
  'plot.title.hilbert': 'Hilbert Transform x̂(t) (90° shift)',
  'plot.title.envelope': 'Envelope |x_a(t)|',
  'plot.title.instPhase': 'Instantaneous Phase ∠x_a(t)',
  'plot.title.zPlane': '|X(z)| Surface (dB)',
  'plot.title.sPlane': '|X(s)| Surface (dB)',
  'plot.axis.reZ': 'Re(z)',
  'plot.axis.imZ': 'Im(z)',
  'plot.axis.sigma': 'σ',
  'plot.axis.omegaShort': 'ω',
  'plot.axis.xhat': 'x̂(t)',

  // ================= Status bar =================
  'status.ready': 'Ready',
  'status.formulaErr': '⚠ Formula error: {err}',
  'status.pipelineOk': '✔ {src} → {trans}  ·  N={N}  ·  {ms} ms',
  'status.pipelineErr': '✖ {msg}',
  'status.presetLoaded': 'Preset loaded: {name}',
  'status.shareCopied': '✔ Share URL copied: {url}…',
  'status.pngExported': '✔ Exported {n} PNG(s)',
  'status.csvExported': '✔ CSV exported',

  // ================= Sources =================
  'source.sine.name': 'Sine Wave',
  'source.sine.desc': 'A · sin(2π·f·t + φ)',
  'source.square.name': 'Square Wave',
  'source.square.desc': 'Ideal square wave (rich odd harmonics)',
  'source.triangle.name': 'Triangle Wave',
  'source.triangle.desc': 'Triangle wave',
  'source.sawtooth.name': 'Sawtooth Wave',
  'source.sawtooth.desc': 'Sawtooth wave',
  'source.gaussian.name': 'Gaussian Pulse',
  'source.gaussian.desc': 'Gaussian-modulated pulse (radar / comms)',
  'source.noise.name': 'White Noise',
  'source.noise.desc': 'Uniform white noise',
  'source.chirp.name': 'Linear Chirp',
  'source.chirp.desc': 'Frequency sweeps linearly from f0 to f1 — perfect for spectrograms',
  'source.formula.name': '✏️ Custom Formula (LaTeX)',
  'source.formula.desc': 'Write any LaTeX expression — sin/cos/exp/sqrt/sinc/u(t)/rect/tri all supported',

  // Common parameter labels
  'param.freq': 'Frequency',
  'param.amp': 'Amplitude',
  'param.phase': 'Phase',
  'param.center': 'Center',
  'param.sigma': 'Sigma',
  'param.f0': 'Start Freq',
  'param.f1': 'End Freq',
  'param.sampleRate': 'Sample Rate',
  'param.duration': 'Duration',
  'param.numSamples': 'Num Samples',
  'param.kind': 'Signal Kind',
  'param.latex': 'LaTeX Expression',
  'param.kind.continuous': 'Continuous x(t)',
  'param.kind.discrete': 'Discrete x[n]',
  'param.sampleRate.tip': 'Sample rate fs; t and n are linked by t = n/fs',
  'param.duration.tip': 'Continuous mode only',
  'param.numSamples.tip': 'Discrete mode only: sequence length N',

  // Transform-specific param labels
  'param.fmax': 'Max Frequency',
  'param.numFreqs': 'Frequency Points',
  'param.numFreqs.dtft': 'Frequency Resolution',
  'param.numFreqs.dtft.desc': 'Number of points on ω axis (more = closer to true continuous DTFT)',
  'param.period': 'Period T',
  'param.harmonics': 'Harmonics K',
  'param.harmonics.desc': 'Keep ±K harmonics',
  'param.normalize': 'Orthonormal',
  'param.normalize.desc': 'Orthonormal scaling (matches scipy.fft.dct(norm="ortho"))',
  'param.periodsToShow': 'Periods Shown',
  'param.periodsToShow.desc': 'Number of periods drawn',
  'param.frameSize': 'Frame Size',
  'param.hopSize': 'Hop Size',
  'param.sigmaRange': 'σ Range',
  'param.sigmaRange.desc': 'Real-axis sweep range [-σ, σ]',
  'param.omegaRange': 'ω Range',
  'param.gridSize': 'Grid Size',
  'param.gridSize.desc': '2D grid resolution on the complex plane',
  'param.rMax': 'Radius Max',
  'param.fmax.desc': 'Upper limit of frequency sweep',

  // ================= Transforms =================
  'transform.dft.name': 'DFT (FFT)',
  'transform.dft.desc': 'Radix-2 Cooley–Tukey FFT, auto zero-pad; finite discrete sequence → discrete bins',
  'transform.dft.input': 'Discrete finite sequence x[n] of length N',
  'transform.dft.output': 'N discrete bins; bin k corresponds to frequency f_k = k·fs/N',
  'transform.dft.pitfall': 'DFT is the DTFT sampled on a uniform N-point grid; window choice changes energy distribution',

  'transform.dtft.name': 'DTFT (Dense Sampling)',
  'transform.dtft.desc': 'Discrete → continuous: dense sampling on ω∈[-π, π]',
  'transform.dtft.input': 'Absolutely summable discrete sequence x[n]',
  'transform.dtft.output': 'ω is continuous; spectrum is 2π-periodic; rendered on [-π, π]',
  'transform.dtft.pitfall': 'DFT is DTFT sampled at ω_k = 2πk/N; more DFT points → closer to DTFT',

  'transform.ctft.name': 'CTFT (Numerical)',
  'transform.ctft.desc': 'Continuous → continuous: trapezoidal numerical integration over ω',
  'transform.ctft.input': 'Continuous signal x(t) with finite energy',
  'transform.ctft.output': 'Complex spectrum X(jω); magnitude |X(jω)| and phase ∠X(jω)',
  'transform.ctft.pitfall': 'Truncated duration introduces Gibbs ripples; dense sampling approximates continuity',

  'transform.ctfs.name': 'CTFS (Fourier Series)',
  'transform.ctfs.desc': 'Fourier-series expansion of a periodic continuous signal → discrete harmonic coefficients',
  'transform.ctfs.input': 'Periodic signal; user must specify the period T',
  'transform.ctfs.output': 'Discrete harmonic amplitudes (line spectrum at ±k·ω₀)',
  'transform.ctfs.pitfall': 'Wrong period T causes coefficient leakage — the key distinction from CTFT',

  'transform.dfs.name': 'DFS (Discrete Fourier Series)',
  'transform.dfs.desc': 'Fourier series of a periodic discrete signal: periodic → periodic',
  'transform.dfs.input': 'Periodic discrete signal x̃[n] with period N',
  'transform.dfs.output': 'Periodic discrete spectrum X̃[k] (3 periods shown)',
  'transform.dfs.pitfall': 'DFS shares the formula with DFT but acts on infinite-length periodic sequences',

  'transform.dct.name': 'DCT-II',
  'transform.dct.desc': 'Same kernel used in JPEG/MP3; better energy compaction than DFT',
  'transform.dct.input': 'Real-valued finite sequence',
  'transform.dct.output': 'Real coefficients; low-frequency coefficients carry most energy',
  'transform.dct.pitfall': 'DCT-II and DCT-III are mutually inverse; libraries differ on normalization',

  'transform.dst.name': 'DST-I',
  'transform.dst.desc': 'Discrete sine transform; suited for odd-symmetric extension',
  'transform.dst.input': 'Real sequence; endpoints treated as zero',
  'transform.dst.output': 'Real coefficients; complementary to DCT, captures odd-symmetric content',
  'transform.dst.pitfall': 'DST-I/II/III/IV differ in boundary conditions',

  'transform.stft.name': 'Short-Time Fourier Transform',
  'transform.stft.desc': 'Sliding-window FFT producing a spectrogram',
  'transform.stft.input': 'Sequence at least one frame long',
  'transform.stft.output': 'Time–frequency representation; x = time, y = frequency, color = magnitude (dB)',
  'transform.stft.pitfall': 'Frame length vs frequency resolution trade-off; smaller hop → smoother but slower',

  'transform.hilbert.name': 'Hilbert Transform',
  'transform.hilbert.desc': 'Produces the analytic signal; magnitude = envelope, phase = instantaneous phase',
  'transform.hilbert.input': 'Real-valued signal',
  'transform.hilbert.output': 'Complex signal x_a(t) = x(t) + j·x̂(t); |x_a| is envelope, arg(x_a) is instantaneous phase',
  'transform.hilbert.pitfall': 'The Hilbert filter is a 90° phase shifter; edge effects are noticeable',

  'transform.laplace.name': 'Laplace Transform Surface',
  'transform.laplace.desc': '|X(s)| computed over the s-plane grid; the jω axis equals CTFT',
  'transform.laplace.input': 'Causal continuous signal x(t), t ≥ 0',
  'transform.laplace.output': '2D magnitude surface in the s-plane; slice at σ=0 (jω axis) equals CTFT',
  'transform.laplace.pitfall': 'σ must lie inside the region of convergence (ROC)',

  'transform.z.name': 'Z-Transform Surface',
  'transform.z.desc': '|X(z)| computed over the z-plane grid; the unit circle equals DTFT',
  'transform.z.input': 'Causal discrete sequence x[n]',
  'transform.z.output': '2D magnitude surface in the z-plane; slice at r=1 (unit circle) equals DTFT',
  'transform.z.pitfall': 'The region of convergence (ROC) determines stability and causality',

  // ================= Windows =================
  'window.rect': 'Rectangular (None)',
  'window.hann': 'Hann',
  'window.hamming': 'Hamming',
  'window.blackman': 'Blackman',

  // ================= Presets =================
  'preset.aliasing.name': '🌀 Aliasing Demo',
  'preset.aliasing.desc': '60 Hz signal sampled at 80 Hz → spectrum folds back to 20 Hz (aliasing)',
  'preset.beating.name': '🥁 Beat Frequency',
  'preset.beating.desc': '50 Hz + 53 Hz superposition → 3 Hz beat envelope appears',
  'preset.fourierSeries.name': '🎵 Square Wave Spectrum',
  'preset.fourierSeries.desc': 'Odd harmonics (1/3/5/7…) of a square wave at a glance',
  'preset.chirpSpec.name': '🚀 Chirp Spectrogram',
  'preset.chirpSpec.desc': 'Chirp spectrogram — frequency increasing linearly with time',
  'preset.dctCompaction.name': '📦 DCT Energy Compaction',
  'preset.dctCompaction.desc': 'Compare DCT vs DFT — DCT concentrates energy in low frequencies, just like JPEG',
  'preset.dtftVsDft.name': '🔍 DTFT vs DFT',
  'preset.dtftVsDft.desc': 'DTFT is continuous, DFT is its N-point sampling — try switching',
  'preset.formulaSin.name': '✏️ Formula: 2-tone',
  'preset.formulaSin.desc': 'Two sinusoids written as LaTeX; verify the spectrum',
  'preset.formulaGauss.name': '✏️ Formula: Gaussian',
  'preset.formulaGauss.desc': 'Gaussian e^{-t²/0.005} — verifies "Gaussian ↔ Gaussian"',
  'preset.hilbert.name': '📈 Hilbert Envelope',
  'preset.hilbert.desc': 'Instantaneous envelope of an AM signal — the classic Hilbert use case',
  'preset.zSurface.name': '🌐 Z-Plane Surface',
  'preset.zSurface.desc': 'Visualise |X(z)|; the unit circle is DTFT magnitude',

  // ================= Cheatsheet =================
  'cs.title': '📚 Cheatsheet',
  'cs.close': 'Close',
  'cs.cat.definitions': 'Transform Definitions',
  'cs.cat.properties': 'Properties',
  'cs.cat.pairs': 'Common Pairs',
  'cs.cat.discretePairs': 'Discrete Pairs',

  // Notes (keyed by id)
  'cs.note.ctft': 'CTFT — Continuous-Time Fourier Transform',
  'cs.note.dtft': 'DTFT — output is continuous and 2π-periodic',
  'cs.note.dft': 'DFT — DTFT sampled on N points',
  'cs.note.ctfs': 'CTFS — Fourier-series coefficients of a periodic signal',
  'cs.note.laplace': 'Laplace — s-plane analysis (generalises CTFT)',
  'cs.note.zt': 'Z-Transform — discrete counterpart of Laplace',
  'cs.note.dct': 'DCT-II — same kernel as JPEG/MP3',
  'cs.note.hilbert': 'Hilbert — 90° phase shift; produces the analytic signal',
  'cs.note.linearity': 'Linearity',
  'cs.note.timeshift': 'Time shift → linear phase slope',
  'cs.note.modulation': 'Frequency shift (modulation)',
  'cs.note.scaling': 'Scaling → time–frequency reciprocity',
  'cs.note.derivative': 'Differentiation → high-frequency emphasis',
  'cs.note.convolution': 'Convolution ↔ multiplication',
  'cs.note.parseval': 'Parseval — energy conservation',
  'cs.note.delta': 'Impulse ↔ flat spectrum',
  'cs.note.dc': 'DC ↔ impulse at zero frequency',
  'cs.note.expDecay': 'Exponential decay ↔ first-order low-pass form',
  'cs.note.rectSinc': 'Rectangular pulse ↔ sinc',
  'cs.note.cosine': 'Cosine ↔ pair of impulses at ±ω₀',
  'cs.note.gauss': 'Gaussian ↔ Gaussian (self-dual)',
  'cs.note.delta_n': 'Unit impulse ↔ flat spectrum',
  'cs.note.geom': 'Geometric sequence',
  'cs.note.discCos': 'Discrete cosine ↔ periodic impulse train'
};

const zh: Dict = {
  // ================= Header =================
  'header.title': '信号 Playground',
  'header.tagline': '面向 EE/CS 学习者的交互式信号与频谱可视化工具',
  'header.domain.signal': '📈 信号',
  'header.domain.image': '🖼️ 图像',
  'header.domain.image.tip': '将于 v0.3 推出',
  'header.preset.placeholder': '— 教学预设 —',
  'header.preset.tip': '加载教学预设',
  'header.btn.cheatsheet': '📚 速查表',
  'header.btn.cheatsheet.tip': '展开公式速查表',
  'header.btn.share': '🔗 分享',
  'header.btn.share.tip': '复制可分享的 URL',
  'header.btn.png': '⬇ PNG',
  'header.btn.png.tip': '导出图表为 PNG',
  'header.btn.csv': '⬇ CSV',
  'header.btn.csv.tip': '导出数据为 CSV',
  'header.btn.github': 'GitHub ↗',
  'header.btn.lang': 'EN',
  'header.btn.lang.tip': '切换到英文',
  'footer.ready': '就绪',
  'footer.meta': '为 EE/CS 学习者用 ❤ 打造 · MIT 协议',

  // ================= Panel sections =================
  'panel.section.kind': '🧬 信号类型',
  'panel.section.source': '🌊 信号源',
  'panel.section.window': '🪟 窗函数',
  'panel.section.transform': '🔁 变换',
  'panel.section.tfInfo': '📐 变换定义',
  'panel.btn.reset': '↺ 重置',
  'panel.kind.continuous': '∿ 连续',
  'panel.kind.discrete': '┊ 离散',
  'panel.kind.continuous.tip': '连续信号 x(t)：以高密度采样近似',
  'panel.kind.discrete.tip': '离散信号 x[n]：以茎图渲染',
  'panel.kind.note.discrete': '<b>x[n]</b>，N = <b>{N}</b>，fs = <b>{fs} Hz</b> &rarr; t = n / fs &isin; [0, {tMax}] s。',
  'panel.kind.note.continuous': '<b>x(t)</b>，t &isin; [0, {dur}] s，采样率 <b>{fs} Hz</b>（N = {N}）。',
  'panel.transform.incompatible': '✗（与 {kind} 模式不兼容）',
  'panel.transform.noParams': '该变换无可调参数。',

  // Transform group labels
  'group.fourier-discrete': '— 傅里叶（离散）—',
  'group.fourier-continuous': '— 傅里叶（连续）—',
  'group.cosine-sine': '— 余弦 / 正弦 —',
  'group.time-frequency': '— 时频分析 —',
  'group.analytic': '— 解析信号 —',
  'group.complex-plane': '— 复平面 —',

  // Transform info tags
  'tf.tag.input': '输入',
  'tf.tag.output': '输出',
  'tf.tag.pitfall': '易错点',
  'tf.tag.related': '相关',

  // ================= Formula input =================
  'formula.title': '✏️ 公式输入（LaTeX）',
  'formula.placeholder': '示例：\\sin(2\\pi \\cdot 5 t) + 0.5\\cos(2\\pi \\cdot 12 t)',
  'formula.invalid': '（LaTeX 无效）',
  'formula.banner.discrete': `<strong>┊ 离散  x[n]</strong>
        使用 <code>n</code> 表示 <b>整数索引</b>（0, 1, 2, …），
        使用 <code>t</code> 表示 <b>物理时间（秒）</b>（t = n / fs）。
        例如：<code>\\sin(0.5\\pi n)</code> → 周期为 4 的离散正弦；
        <code>\\sin(2\\pi \\cdot 5 t)</code> → 5 Hz 正弦的离散采样。
        序列长度由 <b>采样点数</b> 决定。`,
  'formula.banner.continuous': `<strong>∿ 连续  x(t)</strong>
        变量 <code>t</code> 是 <b>物理时间（秒）</b>，范围 0 → 时长；
        <code>n</code> 是整数索引（n = t · fs）。
        例如：<code>\\sin(2\\pi \\cdot 5 t)</code> → 5 Hz 正弦。`,

  // ================= Plot titles / axes =================
  'plot.timeDomain.continuous': '时域  x(t)',
  'plot.timeDomain.discrete': '时域  x[n]   （N = {N}）',
  'plot.axis.time': '时间 (s)',
  'plot.axis.sampleIndex': 'n（采样索引）',
  'plot.axis.amplitude': '幅度',
  'plot.axis.freqHz': '频率 (Hz)',
  'plot.axis.omega': 'ω (rad/sample)',
  'plot.axis.harmonic': '谐波次数 k',
  'plot.axis.kExtended': 'k（扩展显示）',
  'plot.axis.coeffIndex': '系数索引 k',
  'plot.axis.value': '取值',
  'plot.axis.magDb': '幅度 (dB)',
  'plot.axis.magCk': '|c_k|',
  'plot.axis.phaseRad': '相位 (rad)',
  'plot.axis.envelope': '包络',
  'plot.title.magSpectrum': '幅度谱',
  'plot.title.phaseSpectrum': '相位谱',
  'plot.title.coefficients': '变换系数',
  'plot.title.spectrogram': '频谱图 (dB)',
  'plot.title.spectrogramShort': '频谱图',
  'plot.title.hilbert': '希尔伯特变换 x̂(t)（90° 相移）',
  'plot.title.envelope': '包络 |x_a(t)|',
  'plot.title.instPhase': '瞬时相位 ∠x_a(t)',
  'plot.title.zPlane': '|X(z)| 曲面 (dB)',
  'plot.title.sPlane': '|X(s)| 曲面 (dB)',
  'plot.axis.reZ': 'Re(z)',
  'plot.axis.imZ': 'Im(z)',
  'plot.axis.sigma': 'σ',
  'plot.axis.omegaShort': 'ω',
  'plot.axis.xhat': 'x̂(t)',

  // ================= Status bar =================
  'status.ready': '就绪',
  'status.formulaErr': '⚠ 公式错误：{err}',
  'status.pipelineOk': '✔ {src} → {trans}  ·  N={N}  ·  {ms} ms',
  'status.pipelineErr': '✖ {msg}',
  'status.presetLoaded': '已加载预设：{name}',
  'status.shareCopied': '✔ 分享链接已复制：{url}…',
  'status.pngExported': '✔ 已导出 {n} 张 PNG',
  'status.csvExported': '✔ CSV 已导出',

  // ================= Sources =================
  'source.sine.name': '正弦波',
  'source.sine.desc': 'A · sin(2π·f·t + φ)',
  'source.square.name': '方波',
  'source.square.desc': '理想方波（含丰富奇次谐波）',
  'source.triangle.name': '三角波',
  'source.triangle.desc': '三角波',
  'source.sawtooth.name': '锯齿波',
  'source.sawtooth.desc': '锯齿波',
  'source.gaussian.name': '高斯脉冲',
  'source.gaussian.desc': '高斯调制脉冲（雷达 / 通信常用）',
  'source.noise.name': '白噪声',
  'source.noise.desc': '均匀白噪声',
  'source.chirp.name': '线性调频（Chirp）',
  'source.chirp.desc': '频率从 f0 线性扫至 f1，频谱图演示利器',
  'source.formula.name': '✏️ 自定义公式（LaTeX）',
  'source.formula.desc': '任意 LaTeX 表达式 — 支持 sin/cos/exp/sqrt/sinc/u(t)/rect/tri 等',

  // Common parameter labels
  'param.freq': '频率',
  'param.amp': '振幅',
  'param.phase': '相位',
  'param.center': '中心',
  'param.sigma': '宽度 σ',
  'param.f0': '起始频率',
  'param.f1': '终止频率',
  'param.sampleRate': '采样率',
  'param.duration': '时长',
  'param.numSamples': '采样点数',
  'param.kind': '信号类型',
  'param.latex': 'LaTeX 表达式',
  'param.kind.continuous': '连续 x(t)',
  'param.kind.discrete': '离散 x[n]',
  'param.sampleRate.tip': '采样率 fs；t 与 n 由 t = n/fs 关联',
  'param.duration.tip': '仅连续模式生效',
  'param.numSamples.tip': '仅离散模式：序列长度 N',

  // Transform-specific param labels
  'param.fmax': '最大频率',
  'param.numFreqs': '频率点数',
  'param.numFreqs.dtft': '频率分辨率',
  'param.numFreqs.dtft.desc': 'ω 轴采样点数（越多越接近真实连续 DTFT）',
  'param.period': '周期 T',
  'param.harmonics': '谐波数 K',
  'param.harmonics.desc': '保留 ±K 次谐波',
  'param.normalize': '正交归一',
  'param.normalize.desc': '正交归一化（与 scipy.fft.dct(norm="ortho") 一致）',
  'param.periodsToShow': '展示周期数',
  'param.periodsToShow.desc': '可视化展示的周期数',
  'param.frameSize': '帧长',
  'param.hopSize': '步长',
  'param.sigmaRange': 'σ 范围',
  'param.sigmaRange.desc': '实轴扫描范围 [-σ, σ]',
  'param.omegaRange': 'ω 范围',
  'param.gridSize': '网格分辨率',
  'param.gridSize.desc': '复平面 2D 网格分辨率',
  'param.rMax': '最大半径',
  'param.fmax.desc': '扫描频率上限',

  // ================= Transforms =================
  'transform.dft.name': 'DFT（FFT）',
  'transform.dft.desc': '基-2 Cooley–Tukey FFT，自动 zero-pad；离散有限序列 → 离散频点',
  'transform.dft.input': '离散有限长序列 x[n]，长度 N',
  'transform.dft.output': 'N 个离散频点；第 k 点对应频率 f_k = k·fs/N',
  'transform.dft.pitfall': 'DFT 是 DTFT 在 N 点上的均匀采样；窗函数会改变能量分布',

  'transform.dtft.name': 'DTFT（密集采样）',
  'transform.dtft.desc': '离散→连续：在 ω∈[-π, π] 上密集采样的连续频谱',
  'transform.dtft.input': '绝对可和的离散序列 x[n]',
  'transform.dtft.output': 'ω 是连续变量，频谱周期 2π；这里在 [-π, π] 上密集采样可视化',
  'transform.dtft.pitfall': 'DFT 是 DTFT 在 ω_k = 2πk/N 处的采样；DFT 点数越多越接近 DTFT',

  'transform.ctft.name': 'CTFT（数值积分）',
  'transform.ctft.desc': '连续→连续：在 ω 扫描域上做梯形积分',
  'transform.ctft.input': '连续信号 x(t)，能量有限',
  'transform.ctft.output': '复数频谱 X(jω)；幅度 |X(jω)| 与相位 ∠X(jω)',
  'transform.ctft.pitfall': '截断有限时长会引入吉布斯现象；高密度采样近似连续',

  'transform.ctfs.name': 'CTFS（傅里叶级数）',
  'transform.ctfs.desc': '连续周期信号的傅里叶级数展开 → 离散谐波系数',
  'transform.ctfs.input': '周期信号；用户需要指定基频周期 T',
  'transform.ctfs.output': '离散谐波幅度（在 ±k·ω₀ 处的"线状谱"）',
  'transform.ctfs.pitfall': '周期 T 设置错误会导致系数泄漏；这是 CTFS 与 CTFT 的关键区别',

  'transform.dfs.name': 'DFS（离散傅里叶级数）',
  'transform.dfs.desc': '周期离散信号的傅里叶级数：周期 → 周期',
  'transform.dfs.input': '周期为 N 的离散信号 x̃[n]',
  'transform.dfs.output': '周期为 N 的离散谱 X̃[k]；这里展示三个周期',
  'transform.dfs.pitfall': 'DFS 与 DFT 数学公式相同，但 DFS 的对象是无限长周期序列',

  'transform.dct.name': 'DCT-II',
  'transform.dct.desc': 'JPEG/MP3 同款；能量集中性比 DFT 更优',
  'transform.dct.input': '实值有限序列',
  'transform.dct.output': '实数系数；低频系数承载主要能量（能量集中）',
  'transform.dct.pitfall': 'DCT-II 与 DCT-III 互为逆变换；不同库的归一化约定不同',

  'transform.dst.name': 'DST-I',
  'transform.dst.desc': '离散正弦变换；适用奇对称延拓信号',
  'transform.dst.input': '实序列；端点视为 0',
  'transform.dst.output': '实数系数；与 DCT 互补，反映奇对称分量',
  'transform.dst.pitfall': 'DST-I/II/III/IV 各类边界条件不同',

  'transform.stft.name': '短时傅里叶变换',
  'transform.stft.desc': '滑窗 FFT 输出频谱图（spectrogram）',
  'transform.stft.input': '足够长的序列（≥ 一个 frame 长度）',
  'transform.stft.output': '时-频联合表示；横轴时间、纵轴频率、颜色为幅度（dB）',
  'transform.stft.pitfall': '帧长 vs 频率分辨率的权衡；hop 越小越平滑但更慢',

  'transform.hilbert.name': '希尔伯特变换',
  'transform.hilbert.desc': '产生解析信号；幅度即包络，相位即瞬时相位',
  'transform.hilbert.input': '实信号',
  'transform.hilbert.output': '复信号 x_a(t) = x(t) + j·x̂(t)；|x_a| 是包络，arg(x_a) 是瞬时相位',
  'transform.hilbert.pitfall': '希尔伯特滤波器是 90° 相移器；端点效应明显',

  'transform.laplace.name': '拉普拉斯变换曲面',
  'transform.laplace.desc': '在 s 平面网格上计算 |X(s)|；jω 轴 = CTFT',
  'transform.laplace.input': '因果连续信号 x(t)，t ≥ 0',
  'transform.laplace.output': 's 平面 2D 幅度面；σ=0（jω 轴）上等价于 CTFT',
  'transform.laplace.pitfall': 'σ 必须落入收敛域 (ROC) 才有意义',

  'transform.z.name': 'Z 变换曲面',
  'transform.z.desc': '在 z 平面网格上计算 |X(z)|；单位圆 = DTFT',
  'transform.z.input': '离散因果序列 x[n]',
  'transform.z.output': 'z 平面 2D 幅度面；单位圆（r=1）上的切片就是 DTFT',
  'transform.z.pitfall': '收敛域 (ROC) 决定信号是否稳定 / 因果',

  // ================= Windows =================
  'window.rect': '矩形窗（无）',
  'window.hann': 'Hann 窗',
  'window.hamming': 'Hamming 窗',
  'window.blackman': 'Blackman 窗',

  // ================= Presets =================
  'preset.aliasing.name': '🌀 混叠演示',
  'preset.aliasing.desc': '60 Hz 信号用 80 Hz 采样 → 频谱在 20 Hz 处折叠（混叠）',
  'preset.beating.name': '🥁 拍频',
  'preset.beating.desc': '50 Hz + 53 Hz 叠加 → 出现 3 Hz 拍频包络',
  'preset.fourierSeries.name': '🎵 方波频谱',
  'preset.fourierSeries.desc': '方波的奇次谐波（1/3/5/7…）一览无遗',
  'preset.chirpSpec.name': '🚀 Chirp 频谱图',
  'preset.chirpSpec.desc': 'Chirp 信号的频谱图：频率随时间线性增长',
  'preset.dctCompaction.name': '📦 DCT 能量集中',
  'preset.dctCompaction.desc': '对比 DCT vs DFT — DCT 把能量集中到低频，正是 JPEG 的秘诀',
  'preset.dtftVsDft.name': '🔍 DTFT vs DFT',
  'preset.dtftVsDft.desc': 'DTFT 是连续谱，DFT 是其在 N 点上的采样 — 切换试试看',
  'preset.formulaSin.name': '✏️ 公式：双正弦',
  'preset.formulaSin.desc': '用 LaTeX 输入两个正弦的叠加，验证频谱',
  'preset.formulaGauss.name': '✏️ 公式：高斯',
  'preset.formulaGauss.desc': '高斯函数 e^{-t²/0.005} — 验证 "高斯 ↔ 高斯"',
  'preset.hilbert.name': '📈 希尔伯特包络',
  'preset.hilbert.desc': '调幅信号的瞬时包络 — 经典希尔伯特应用',
  'preset.zSurface.name': '🌐 Z 平面曲面',
  'preset.zSurface.desc': '可视化 |X(z)|；单位圆上就是 DTFT 幅度',

  // ================= Cheatsheet =================
  'cs.title': '📚 速查表',
  'cs.close': '关闭',
  'cs.cat.definitions': '变换定义',
  'cs.cat.properties': '常用性质',
  'cs.cat.pairs': '常用变换对',
  'cs.cat.discretePairs': '离散变换对',

  // Notes
  'cs.note.ctft': 'CTFT — 连续时间傅里叶变换',
  'cs.note.dtft': 'DTFT — 输出连续，周期 2π',
  'cs.note.dft': 'DFT — DTFT 在 N 点上的采样',
  'cs.note.ctfs': 'CTFS — 周期信号的傅里叶级数系数',
  'cs.note.laplace': 'Laplace — s 平面分析（CTFT 的推广）',
  'cs.note.zt': 'Z 变换 — 离散版的 Laplace',
  'cs.note.dct': 'DCT-II — JPEG/MP3 同款',
  'cs.note.hilbert': 'Hilbert — 90° 相移；产生解析信号',
  'cs.note.linearity': '线性',
  'cs.note.timeshift': '时移 → 相位线性斜率',
  'cs.note.modulation': '频移（调制）',
  'cs.note.scaling': '尺度变换 → 时频反比',
  'cs.note.derivative': '微分 → 高频强调',
  'cs.note.convolution': '卷积 ↔ 乘积',
  'cs.note.parseval': 'Parseval 能量守恒',
  'cs.note.delta': '冲激 ↔ 全频带白色',
  'cs.note.dc': '直流 ↔ 零频冲激',
  'cs.note.expDecay': '指数衰减 ↔ 一阶低通形',
  'cs.note.rectSinc': '矩形脉冲 ↔ sinc',
  'cs.note.cosine': '余弦 ↔ ±ω₀ 双冲激',
  'cs.note.gauss': '高斯 ↔ 高斯（自共轭）',
  'cs.note.delta_n': '单位脉冲 ↔ 平坦谱',
  'cs.note.geom': '几何序列',
  'cs.note.discCos': '离散余弦 ↔ 周期冲激串'
};

export const DICT: Record<Lang, Dict> = { en, zh };
